import { BUG_BENCH_PATH } from '@/constants/bug-bench';

const COMPARE_SEPARATOR = '-vs-';

// Public names people search for; other harness keys are already readable.
const HARNESS_SLUG_OVERRIDES: Record<string, string> = {
  'codex-cli': 'codex',
};

/** URL-safe slug for a canonical model key (context suffixes become `-1m`). */
export function toModelSlug(modelKey: string): string {
  return modelKey
    .toLowerCase()
    .replace(/\[(\d+[km])\]$/, '-$1')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function toHarnessSlug(harnessKey: string): string {
  return HARNESS_SLUG_OVERRIDES[harnessKey] ?? toModelSlug(harnessKey);
}

/** Short, stable base-36 FNV-1a hash of a key, for disambiguating slugs. */
function hashKey(key: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(36).padStart(7, '0').slice(-6);
}

/**
 * One slug per key, unique within the list. Distinct keys can normalize to the
 * same slug (`foo[1m]` and `foo-1m`), and one page would shadow the other. The
 * key already spelled like the slug keeps it, otherwise the key that sorts
 * first; every other colliding key gets a short hash of itself appended, so
 * each slug depends only on its own key and the keys it collides with.
 */
export function assignUniqueSlugs(
  keys: readonly string[],
  toSlug: (key: string) => string,
): Map<string, string> {
  const keysBySlug = new Map<string, string[]>();
  for (const key of new Set(keys)) {
    const slug = toSlug(key);
    keysBySlug.set(slug, [...(keysBySlug.get(slug) ?? []), key]);
  }
  const slugByKey = new Map<string, string>();
  for (const [slug, colliding] of keysBySlug) {
    const owner =
      colliding.find((key) => key === slug) ?? [...colliding].sort((a, b) => (a < b ? -1 : 1))[0]!;
    for (const key of colliding) {
      slugByKey.set(key, key === owner ? slug : `${slug}-${hashKey(key)}`);
    }
  }
  return slugByKey;
}

export const bugBenchPaths = {
  home: BUG_BENCH_PATH,
  markdown: `${BUG_BENCH_PATH}.md`,
  model: (slug: string) => `${BUG_BENCH_PATH}/models/${slug}`,
  models: `${BUG_BENCH_PATH}/models`,
  provider: (slug: string) => `${BUG_BENCH_PATH}/providers/${slug}`,
  compare: (firstSlug: string, secondSlug: string) =>
    `${BUG_BENCH_PATH}/compare/${getComparePair(firstSlug, secondSlug)}`,
  compareIndex: `${BUG_BENCH_PATH}/compare`,
  harness: (slug: string) => `${BUG_BENCH_PATH}/harnesses/${slug}`,
  harnesses: `${BUG_BENCH_PATH}/harnesses`,
  language: (slug: string) => `${BUG_BENCH_PATH}/languages/${slug}`,
  languages: `${BUG_BENCH_PATH}/languages`,
  bugType: (slug: string) => `${BUG_BENCH_PATH}/bugs/${slug}`,
  bugTypes: `${BUG_BENCH_PATH}/bugs`,
  price: `${BUG_BENCH_PATH}/price`,
  trends: `${BUG_BENCH_PATH}/trends`,
  launches: `${BUG_BENCH_PATH}/launches`,
  edition: (month: string) => `${BUG_BENCH_PATH}/editions/${month}`,
  editions: `${BUG_BENCH_PATH}/editions`,
  stats: `${BUG_BENCH_PATH}/stats`,
  methodology: `${BUG_BENCH_PATH}#methodology`,
  dataJson: `${BUG_BENCH_PATH}/data.json`,
  dataCsv: `${BUG_BENCH_PATH}/data.csv`,
  feed: `${BUG_BENCH_PATH}/feed.xml`,
  badge: (slug: string) => `${BUG_BENCH_PATH}/badge/${slug}`,
} as const;

/** One canonical URL per pair regardless of argument order. */
export function getComparePair(firstSlug: string, secondSlug: string): string {
  const [a, b] = [firstSlug, secondSlug].sort();
  return `${a}${COMPARE_SEPARATOR}${b}`;
}

/**
 * The canonical spelling of `a-vs-b` when it can be told without the
 * snapshot's slugs: exactly one separator between two non-empty slugs, in any
 * letter case. Null when a slug could itself contain the separator.
 */
export function canonicalizeComparePair(pair: string): string | null {
  const slugs = pair.toLowerCase().split(COMPARE_SEPARATOR);
  if (slugs.length !== 2 || !slugs[0] || !slugs[1]) {
    return null;
  }
  return getComparePair(slugs[0], slugs[1]);
}

/**
 * Splits `a-vs-b` against known slugs. Slugs may themselves contain `-vs-`,
 * so every separator position is tried and only a split into two known,
 * different slugs is accepted.
 */
export function parseComparePair(
  pair: string,
  knownSlugs: ReadonlySet<string>,
): [string, string] | null {
  let index = pair.indexOf(COMPARE_SEPARATOR);
  while (index !== -1) {
    const first = pair.slice(0, index);
    const second = pair.slice(index + COMPARE_SEPARATOR.length);
    if (first !== second && knownSlugs.has(first) && knownSlugs.has(second)) {
      return [first, second];
    }
    index = pair.indexOf(COMPARE_SEPARATOR, index + 1);
  }
  return null;
}

export function isEditionMonth(value: string): boolean {
  return /^\d{4}-(?:0[1-9]|1[0-2])$/.test(value);
}
